import React, { useCallback, useContext, useMemo, useRef, useState } from 'react';
import { ApiContext } from '@api/api';
import { CalendarLayoutPayloadDTO, CalendarLayoutResolutionOutDTO, EffectiveCalendarLayoutOutDTO } from '@api/ringil-api.v2';
import { createLogger } from '@utils/logger';
import { CalendarLayoutConfig } from './model';
import { BUILT_IN_LAYOUT_CONFIG } from './defaults';
import { configFromPayload } from './payload';
import { CalendarLayoutContext, CalendarLayoutContextValue } from './context';

const log = createLogger('calendar-layout');

interface CalendarLayoutProviderProps {
  children: React.ReactNode;
}

interface EffectiveState {
  /** Keyed by modelId, as the backend returns it. */
  configs: Record<string, CalendarLayoutConfig>;
  systemDefault: CalendarLayoutResolutionOutDTO | null;
  /** Overrides with a `locationId`. */
  byLocation: Record<string, CalendarLayoutResolutionOutDTO>;
  /** The company-wide override (`locationId` null), if there is one. */
  companyWide: CalendarLayoutResolutionOutDTO | null;
}

const EMPTY_STATE: EffectiveState = { configs: {}, systemDefault: null, byLocation: {}, companyWide: null };

const toState = (data: EffectiveCalendarLayoutOutDTO): EffectiveState => {
  const configs: Record<string, CalendarLayoutConfig> = {};
  Object.entries((data.configs ?? {}) as Record<string, CalendarLayoutPayloadDTO>).forEach(([modelId, payload]) => {
    const config = configFromPayload(payload);
    // A payload this build cannot make sense of is dropped, not rendered as an empty card: the
    // resolution then falls through to the next level, which is the safer wrong answer.
    if (config) configs[modelId] = config;
  });
  const byLocation: Record<string, CalendarLayoutResolutionOutDTO> = {};
  let companyWide: CalendarLayoutResolutionOutDTO | null = null;
  (data.overrides ?? []).forEach(override => {
    if (override.locationId) byLocation[override.locationId] = override;
    else companyWide = override;
  });
  return { configs, systemDefault: data.default ?? null, byLocation, companyWide };
};

/**
 * Serves the calendar the layout each event should render with.
 *
 * One bootstrap call (`effective`) brings the system default plus every override that applies to the
 * caller's company; resolution per event is then a map lookup, which matters because a single
 * calendar view shows ramps from several locations at once.
 */
export const CalendarLayoutProvider: React.FC<CalendarLayoutProviderProps> = ({ children }) => {
  const { listEffectiveCalendarLayouts } = useContext(ApiContext);
  const [state, setState] = useState<EffectiveState>(EMPTY_STATE);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  // Guards against the double-invoked effects of StrictMode and against several consumers mounting
  // at once, both of which would otherwise fire the bootstrap call more than once.
  const inFlightRef = useRef(false);

  const fetch = useCallback(() => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setLoading(true);
    const settle = () => {
      inFlightRef.current = false;
      setLoading(false);
      setLoaded(true);
    };
    listEffectiveCalendarLayouts(
      data => {
        const next = toState(data);
        // Logged because the failure mode here is invisible by design: with no default and no
        // overrides the calendar silently renders the built-in layout, which looks identical to
        // "the feature is not working". This line says which of the two it is.
        log.debug('effective layouts loaded', {
          models: Object.keys(next.configs).length,
          systemDefault: next.systemDefault?.modelName ?? null,
          companyWide: next.companyWide?.modelName ?? null,
          locationOverrides: Object.keys(next.byLocation).length,
        });
        setState(next);
        settle();
      },
      undefined,
      {
        // Passing onError suppresses the standard error toast on purpose: a user without the
        // calendar-layout permission still gets a working calendar — on the built-in layout —
        // rather than an error popup they can do nothing about. It is logged, not swallowed.
        onError: error => {
          log.error('effective layouts unavailable — falling back to the built-in layout', error);
          settle();
        },
      },
    );
  }, [listEffectiveCalendarLayouts]);

  // `fetch` collapses concurrent calls, so a StrictMode double mount is one request while a second
  // visit to the calendar is a genuinely fresh read.
  const reload = useCallback(() => fetch(), [fetch]);

  const value = useMemo<CalendarLayoutContextValue>(() => {
    const configOf = (resolution: CalendarLayoutResolutionOutDTO | null): CalendarLayoutConfig | null =>
      resolution ? (state.configs[resolution.modelId] ?? null) : null;

    // Most specific wins; anything that fails to resolve falls through to the layout the calendar
    // has always drawn.
    const resolutionFor = (locationId?: string): CalendarLayoutResolutionOutDTO | null => {
      const candidates = [locationId ? state.byLocation[locationId] : null, state.companyWide, state.systemDefault];
      return candidates.find(candidate => candidate && configOf(candidate)) ?? null;
    };

    return {
      loading,
      loaded,
      layoutFor: locationId => configOf(resolutionFor(locationId)) ?? BUILT_IN_LAYOUT_CONFIG,
      resolutionFor,
      defaultConfig: configOf(state.systemDefault) ?? BUILT_IN_LAYOUT_CONFIG,
      reload,
    };
  }, [state, loading, loaded, reload]);

  return <CalendarLayoutContext.Provider value={value}>{children}</CalendarLayoutContext.Provider>;
};
